import Vue from "vue";
import "./pollyfills";
import VueRouter from "vue-router";
import VueNotify from "vue-notifyjs";
import VeeValidate from "vee-validate";
import axios from "axios";
import VueAxios from "vue-axios";
import Storage from "vue-ls";
import VueMoment from "vue-moment";
import { ServerTable } from "vue-tables-2-premium";
import lang from "element-ui/lib/locale/lang/en";
import locale from "element-ui/lib/locale";
import App from "./App.vue";
import { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import FlagIcon from "vue-flag-icon";
import VueTelInput from "vue-tel-input";
import Es6Promise from "es6-promise";
import VueSweetalert2 from "vue-sweetalert2";
import VueCurrencyInput from "vue-currency-input";
import VuePapaParse from "vue-papa-parse";
import VueClipboard from "vue-clipboard2";
import store from "./store";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import getDeviceId from "./device";

import "sweetalert2/dist/sweetalert2.min.css";
import "vue-tel-input/dist/vue-tel-input.css";

// ES6 Promise Polyfill
Es6Promise.polyfill();

// Import Helpers for filters
import {
  domain,
  count,
  pluralize,
  money,
  currencySymbol,
  humanize,
  percentageChange,
  number
} from "./filters";

// Import Install and register helper items
Vue.filter("count", count);
Vue.filter("domain", domain);
Vue.filter("pluralize", pluralize);
Vue.filter("money", money);
Vue.filter("currencySymbol", currencySymbol);
Vue.filter("humanize", humanize);
Vue.filter("percentageChange", percentageChange);
Vue.filter("number", number);

// leaflet: this part resolve an issue where the markers would not appear
delete Icon.Default.prototype._getIconUrl;

Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png")
});

// Plugins
import GlobalComponents from "./globalComponents";
import GlobalDirectives from "./globalDirectives";
import SideBar from "./components/UIComponents/SidebarPlugin";
import initProgress from "./progressbar";

// router setup
import routes from "./routes/routes";

// config setup
import config from "./config.js";

// library imports
import "./assets/sass/paper-dashboard.scss";
import "./assets/sass/element_variables.scss";
import "./assets/sass/demo.scss";
import "./assets/sass/custom.scss";

import sidebarLinks from "./sidebarLinks";

const vueTablesServerOptions = {
  filterByColumn: true,
  saveState: true,
  sortIcon: {
    base: "fa",
    up: "fa-chevron-up",
    down: "fa-chevron-down",
    is: "fa-sort"
  },
  requestAdapter(input) {
    const output = {};

    const itemsPerPage = input.limit;
    const page = input.page;
    const offset = (page - 1) * itemsPerPage;

    // sort
    if (input.orderBy && input.orderBy !== "") {
      let sort = "";
      if (input.ascending === 0) {
        sort = "-"; // desc
      }
      sort += input.orderBy;
      output.sort = sort;
    }

    // paginate (limit and offset)
    output.limit = input.limit;
    output.offset = offset;

    // filter
    _.each(input.query, (val, key) => {
      if (val && val !== "") {
        output[key] = val;
      }
    });

    return output;
  },
  responseAdapter: resp => {
    let count = resp.data.length;
    if (resp.headers["total-count"]) {
      count = parseInt(resp.headers["total-count"]);
    }
    return {
      data: resp.data,
      count
    };
  }
};

window.axios = axios; // for vue tables server table

// plugin setup
Vue.use(VueRouter);
Vue.use(GlobalDirectives);
Vue.use(GlobalComponents);
Vue.use(VueNotify);
Vue.use(SideBar, { sidebarLinks: sidebarLinks });
Vue.use(VeeValidate);
Vue.use(VueAxios, axios);
Vue.use(VueMoment);
Vue.use(Storage, {
  namespace: `${config.namespace}__`, // key prefix
  name: "ls", // name variable Vue.[ls] or this.[$ls],
  storage: "local" // storage name session, local, memory
});
Vue.use(
  ServerTable,
  vueTablesServerOptions,
  false,
  "bootstrap4",
  "default" // "footerPagination"
);
locale.use(lang);
Vue.use(FlagIcon);
Vue.use(VueTelInput);
Vue.use(VuePapaParse);
Vue.use(VueSweetalert2);
Vue.use(VueCurrencyInput);
Vue.use(VueClipboard);

// configure axios
Vue.axios.defaults.baseURL = config.apiBaseUrl;
Vue.axios.defaults.withCredentials = true;

// request interceptor to add operator ID when relevant
Vue.axios.interceptors.request.use(
  function (vueConfig) {
    // if (window.location.hostname === "localhost") {
    //   config.headers["x-apicache-bypass"] = true; // do not use cached responses on localhost
    // }

    vueConfig.headers["x-apicache-bypass"] = true; // do not get cached responses

    if (vueConfig.url.includes("/v1/tokens")) {
      return vueConfig;
    }

    // set token perspective to request headers if set
    const account = store.state.operator;

    if (account && account.id) {
      vueConfig.headers["token-perspective"] = account.id;
    }

    return vueConfig;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

// Function that will be called to refresh authorization
const refreshAuthLogic = failedRequest => {
  return axios
    .post(
      "/v1/tokens",
      {
        type: "access",
        grantType: "refresh_token",
        deviceIdentifier: getDeviceId(),
        attachments: ["operator"]
      },
      { skipAuthRefresh: true }
    )
    .then(response => {
      const expSeconds =
        moment(response.data.expiresAt).unix() - moment().unix();
      Vue.ls.set(
        "access_token_expires_at",
        response.data.expiresAt,
        expSeconds * 1000 // expiry milliseconds
      );
      return Promise.resolve();
    })
    .catch(error => {
      return Promise.reject(error);
    });
};

// Instantiate the interceptor (you can chain it as it returns the axios instance)
createAuthRefreshInterceptor(Vue.axios, refreshAuthLogic, {
  statusCodes: [401, 403],
  pauseInstanceWhileRefreshing: false
});

// Axios response interceptor for expired session
Vue.axios.interceptors.response.use(null, (error) => {
  const logoutMessages = [
    "Token expired",
    "Device identifier required",
    "Invalid device",
    "Refresh token missing"
  ];

  // handle an invalid access token
  if (
    error.response.status === 401 &&
    error.response.data.code === "invalid_token"
  ) {
    // clear the token - this should redirect user to login page
    store.dispatch("logout", null, { root: true });
  }

  if (
    error.response.config.url.endsWith("/v1/tokens") &&
    [401, 400].includes(error.response.status) &&
    logoutMessages.includes(error.response.data.message)
  ) {
    // clear the token - this should redirect user to login page
    store.dispatch("logout", null, { root: true });
  }

  return Promise.reject(error);
});

// configure router
const router = new VueRouter({
  routes,
  linkActiveClass: "active",
  mode: "history",
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        selector: to.hash
      };
    } else if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  }
});

function authExpiredGuard(to, from, next) {
  // check logged in state
  const refreshTokenExpiresAt = Vue.ls.get("refresh_token_expires_at");

  if (
    to.meta.authRequired === true &&
    (!refreshTokenExpiresAt || moment(refreshTokenExpiresAt).isBefore(moment()))
  ) {
    store.dispatch("logout");
    // redirect to login
    return next({ name: "Login" });
  } else {
    return next();
  }
}

function authRequiredGuard(to, from, next) {
  if (!store.state.loggedIn && to.meta.authRequired === true) {
    // redirect to login
    return next({ name: "Login" });
  } else if (to.name === "Login" && store.state.loggedIn) {
    // redirect to dashboard
    return next({ path: "/dashboard" });
  } else {
    return next();
  }
}

router.beforeEach(authExpiredGuard);
router.beforeEach(authRequiredGuard);

Vue.router = router;

initProgress(router);

/* eslint-disable no-new */
new Vue({
  el: "#app",
  render: h => h(App),
  router,
  store
});
